/**
 * Utility functions for handling report downloads
 */

export const MONTH_FILTER_START_YEAR = 2025;
export const MONTH_FILTER_START_MONTH_INDEX = 0;

/**
 * Downloads a blob as a file
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The filename for the download
 */
export const downloadBlob = (blob, filename) => {
  // Create a temporary URL for the blob
  const url = window.URL.createObjectURL(blob);

  // Create a temporary anchor element
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;

  // Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL
  window.URL.revokeObjectURL(url);
};

/**
 * Generates a filename for the report based on type and month
 * @param {string} reportType - The type of report ('executive' or 'performance')
 * @param {string} month - The month in YYYY-MM format
 * @returns {string} The generated filename
 */
export const generateReportFilename = (reportType, month) => {
  const reportNames = {
    executive: "AI_Executive_Report",
    performance: "AI_Performance_Report",
  };

  const reportName = reportNames[reportType] || "AI_Report";
  return `${reportName}_${month}.pdf`;
};

/**
 * Gets the current month in YYYY-MM format
 * @returns {string} Current month in YYYY-MM format
 */
export const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

/**
 * Gets the minimum selectable month date (fixed business rule).
 * @returns {Date} First day of January 2026
 */
export const getMonthFilterMinDate = () =>
  new Date(MONTH_FILTER_START_YEAR, MONTH_FILTER_START_MONTH_INDEX, 1);

/**
 * Gets the maximum selectable month date (current month).
 * @returns {Date} First day of current month
 */
export const getMonthFilterMaxDate = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

/**
 * Clamps a date into the supported month filter range.
 * @param {Date | null | undefined} date
 * @returns {Date}
 */
export const clampDateToMonthFilterRange = (date) => {
  const minDate = getMonthFilterMinDate();
  const maxDate = getMonthFilterMaxDate();

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return maxDate;
  }

  const normalizedDate = new Date(date.getFullYear(), date.getMonth(), 1);

  if (normalizedDate < minDate) {
    return minDate;
  }

  if (normalizedDate > maxDate) {
    return maxDate;
  }

  return normalizedDate;
};

/**
 * Gets the list of available years for the month picker.
 * @returns {number[]}
 */
export const getMonthFilterYears = () => {
  const minYear = MONTH_FILTER_START_YEAR;
  const maxYear = getMonthFilterMaxDate().getFullYear();
  const endYear = Math.max(minYear, maxYear);

  return Array.from({ length: endYear - minYear + 1 }, (_, i) => minYear + i);
};

/**
 * Gets available month indices (0-11) for a given year within filter bounds.
 * @param {number} year
 * @returns {number[]}
 */
export const getSelectableMonthIndices = (year) => {
  const minDate = getMonthFilterMinDate();
  const maxDate = getMonthFilterMaxDate();
  const minYear = minDate.getFullYear();
  const maxYear = maxDate.getFullYear();

  if (year < minYear || year > maxYear) {
    return [];
  }

  const startMonth = year === minYear ? minDate.getMonth() : 0;
  const endMonth = year === maxYear ? maxDate.getMonth() : 11;

  return Array.from(
    { length: endMonth - startMonth + 1 },
    (_, index) => startMonth + index,
  );
};

/**
 * Validates month format (YYYY-MM)
 * @param {string} month - The month to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidMonthFormat = (month) => {
  const monthRegex = /^\d{4}-\d{2}$/;
  return monthRegex.test(month);
};
